import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import messageService from '../../services/messageService';

const initialState = {
  // List of all conversations (e.g., for a sidebar list)
  conversations: [], 
  // ID of the conversation currently being viewed
  currentConversation: null,
  // Messages for the current conversation
  messages: [],
  // Global unread message count
  unreadCount: 0,
  // General loading state for active fetch/send operations
  loading: false,  
  // Specific loading state for the conversation list
  conversationsLoading: false,  
  error: null,
  // Tracks typing status: { userId: boolean }
  typing: {},
  
  // ✅ NEW STATE FIELD: To hold the list of currently online users (e.g., an array of user IDs)
  onlineUsers: [], 
};

// --- Async Thunks (No change needed here) ---

export const sendMessage = createAsyncThunk(
  'messages/send',
  async (messageData, { rejectWithValue }) => {
    try {
      // messageService.sendMessage already returns response.data (the parsed body: {success, data})
      const body = await messageService.sendMessage(messageData);
      return body;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to send message');
    }
  }
);

export const fetchConversation = createAsyncThunk(
  'messages/fetchConversation',
  async ({ conversationId, params }, { rejectWithValue }) => {
    try {
      // messageService already returns response.data (the body: {success, data: {messages, pagination}})
      const body = await messageService.getConversation(conversationId, params);
      return body;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch conversation');
    }
  }
);

export const fetchConversations = createAsyncThunk(
  'messages/fetchConversations',
  async (_, { rejectWithValue }) => {
    try {
      // messageService already returns response.data (the body: {success, data: [...]})
      const body = await messageService.getConversations();
      return body;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch conversations list');
    }
  }
);

export const markMessageAsRead = createAsyncThunk(
  'messages/markAsRead',
  async (messageId, { rejectWithValue }) => {
    try {
      await messageService.markAsRead(messageId);
      return { messageId };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to mark message as read');
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  'messages/fetchUnreadCount',
  async (_, { rejectWithValue }) => {
    try {
      // messageService already returns response.data (the body: {success, data: {count}})
      const body = await messageService.getUnreadCount();
      return body;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch unread count');
    }
  }
);


const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    addMessage: (state, action) => {
      const newMessage = action.payload;
      
      // 1. Add to current message list (if it's the active conversation)
      if (state.currentConversation === newMessage.conversationId && !state.messages.some(msg => (msg.id || msg._id) === (newMessage.id || newMessage._id))) {
        state.messages.push(newMessage);
      }
      
      // 2. Update conversation list (move to top and update last message)
      const conversationIndex = state.conversations.findIndex(c => (c.id || c._id) === newMessage.conversationId);
      if (conversationIndex !== -1) {
        const updatedConversation = { 
          ...state.conversations[conversationIndex], 
          lastMessage: newMessage,
        };
        // Remove from old position and add to the top of the list
        state.conversations.splice(conversationIndex, 1);  
        state.conversations.unshift(updatedConversation);  
      }
    },
    setCurrentConversation: (state, action) => {
      state.currentConversation = action.payload;
      if (action.payload === null) {
        state.messages = [];
      }
    },
    clearMessages: (state) => {
      state.messages = [];
      state.currentConversation = null;
    },
    setTyping: (state, action) => {
      const { userId, isTyping } = action.payload;
      if (isTyping) {
        state.typing[userId] = true;
      } else {
        delete state.typing[userId];
      }
    },
    incrementUnread: (state) => {
      state.unreadCount += 1;
    },
    decrementUnread: (state, action) => {
      state.unreadCount = Math.max(0, state.unreadCount - (action.payload || 1));
    },
    clearError: (state) => {
      state.error = null;
    },
    
    // =================================================================
    // ✅ ADDED ACTIONS TO FIX BUILD ERROR
    // =================================================================
    
    // Action: setOnlineUsers (Fixes the primary build error)
    // Payload: array of online user IDs or objects
    setOnlineUsers: (state, action) => {
      state.onlineUsers = action.payload;
    },

    // Action: setUserOnlineStatus (Logic to update a single user's status)
    // Payload: { userId: string, isOnline: boolean }
    setUserOnlineStatus: (state, action) => {
        const { userId, isOnline } = action.payload;
        
        if (isOnline) {
            if (!state.onlineUsers.includes(userId)) {
              state.onlineUsers.push(userId); // Assuming array stores user IDs
            }
        } else {
            state.onlineUsers = state.onlineUsers.filter(id => id !== userId);
        }
    },

    // Action: markMessagesAsRead (Logic used by the socket listener)
    // Payload: { conversationId: string }
    markMessagesAsRead: (state, action) => {
        const { conversationId } = action.payload;
        
        // Mark messages in the current view as read
        if (state.currentConversation === conversationId) {
          state.messages = state.messages.map(msg => ({
            ...msg,
            read: true
          }));
        }

        // Update the unread count in the conversation list to 0
        const conversationIndex = state.conversations.findIndex(c => (c.id || c._id) === conversationId);
        if (conversationIndex !== -1) {
          // You might need more complex logic here to subtract the actual unread count,
          // but setting to 0 upon receiving the 'messages_read' event is a safe baseline.
          state.conversations[conversationIndex].unreadCount = 0; 
        }
    },

    // Action: revokeMessage (Handles message_revoked socket events)
    // Payload: { messageId, conversationId, reason, violations }
    revokeMessage: (state, action) => {
      const { messageId, reason, violations } = action.payload || {};
      if (!messageId) return;
      state.messages = state.messages.map(msg => {
        if (msg._id === messageId || msg.id === messageId) {
          return {
            ...msg,
            isRevoked: true,
            revokedReason: reason || 'anti_poaching_violation',
            violations: violations || []
          };
        }
        return msg;
      });
      state.conversations = state.conversations.map(conv => {
        if (conv.lastMessage && (conv.lastMessage._id === messageId || conv.lastMessage.id === messageId)) {
          return {
            ...conv,
            lastMessage: {
              ...conv.lastMessage,
              isRevoked: true,
              revokedReason: reason || 'anti_poaching_violation',
              violations: violations || []
            }
          };
        }
        return conv;
      });
    },
  },
  extraReducers: (builder) => {
    builder
      // --- sendMessage ---
      .addCase(sendMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.loading = false;
        // The socket 'message_received' event is only dispatched to the *recipient*.
        // The sender must add the message themselves directly from the API response.
        const newMessage = action.payload?.data;
        if (newMessage) {
          const convId = newMessage.conversationId || newMessage.participants?.join?.('_');
          // Avoid duplicates in case a socket echo ever arrives
          if (!state.messages.some(m => (m.id || m._id) === (newMessage.id || newMessage._id))) {
            state.messages.push(newMessage);
          }
          // Bubble the conversation to the top of the sidebar list
          const idx = state.conversations.findIndex(c => (c.id || c._id) === convId);
          if (idx !== -1) {
            const updated = { ...state.conversations[idx], lastMessage: newMessage };
            state.conversations.splice(idx, 1);
            state.conversations.unshift(updated);
          }
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // --- fetchConversation ---
      .addCase(fetchConversation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConversation.fulfilled, (state, action) => {
        state.loading = false;
        // body shape: { success, data: { messages, pagination } }
        const payload = action.payload?.data || action.payload;
        state.messages = payload?.messages || [];
        state.error = null;
      })
      .addCase(fetchConversation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.messages = [];
      })

      // --- fetchConversations (List) ---
      .addCase(fetchConversations.pending, (state) => {
        state.conversationsLoading = true;
        state.error = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.conversationsLoading = false;
        // body shape: { success, data: [...] }
        state.conversations = action.payload?.data || action.payload || [];
        state.error = null;
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.conversationsLoading = false;
        state.error = action.payload;
        state.conversations = [];
      })

      // --- markMessageAsRead (Thunk) ---
      .addCase(markMessageAsRead.fulfilled, (state) => {
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      })
      
      // --- fetchUnreadCount ---
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        // body shape: { success, data: { count } }
        state.unreadCount = action.payload?.data?.count ?? action.payload?.count ?? 0;
      })
      .addCase(fetchUnreadCount.rejected, (state, action) => {
        console.error('Failed to fetch unread count:', action.payload);
      });
  }
});

// ✅ UPDATED EXPORT LIST: Include all new actions
export const {
  addMessage,
  setCurrentConversation,
  clearMessages,
  setTyping,
  incrementUnread,
  decrementUnread,
  clearError,
  setOnlineUsers, // <-- FIX
  setUserOnlineStatus, // <-- FIX
  markMessagesAsRead, // <-- FIX
  revokeMessage
} = messagesSlice.actions;

export default messagesSlice.reducer;